import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
  commandDefaultFilter,
  useToast,
} from "ui";

import { useLabelsRepository } from "src/contexts/LabelsRepositoryContext";
import { randomLabelColor } from "src/lib/labelColors";

import { type LabelPickerProps } from "./LabelPicker";
import { LabelPickerRow } from "./LabelPickerRow";

export function LabelPicker(props: LabelPickerProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openSelectedIds, setOpenSelectedIds] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const labelsRepository = useLabelsRepository();
  const createLabelMutation = useMutation({
    mutationFn: (name: string) => {
      return labelsRepository.createLabel({
        name,
        description: "",
        color: randomLabelColor(),
      });
    },
    onSuccess: (label) => {
      queryClient.invalidateQueries({
        queryKey: labelsRepository.listOptions.queryKey,
      });
      setSearch("");
      props.onToggle(label.id);
    },
    onError: () => {
      toast.toast({
        title: t("labelPicker.createFailed"),
        variant: "destructive",
      });
    },
  });
  const trimmedSearch = search.trim();
  const canCreate =
    !!props.allowCreate &&
    trimmedSearch.length > 0 &&
    !props.labels.some((label) => {
      return label.name.toLowerCase() === trimmedSearch.toLowerCase();
    });
  const topLabels = props.labels.filter((label) => {
    return openSelectedIds.includes(label.id);
  });
  const bottomLabels = props.labels.filter((label) => {
    return !openSelectedIds.includes(label.id);
  });
  const hasSelection = props.selectedIds.length > 0;

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);

    if (nextOpen) {
      setOpenSelectedIds(props.selectedIds);
    } else {
      setSearch("");
    }
  }

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{props.trigger}</PopoverTrigger>
      {open ? (
        <PopoverContent
          className="w-[300px] p-0"
          align={props.align ?? "start"}
        >
          <Command
            filter={(value, itemSearch, keywords) => {
              return commandDefaultFilter(value, itemSearch.trim(), keywords);
            }}
          >
            <CommandInput
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder={
                props.placeholder ??
                (hasSelection
                  ? t("labelPicker.changeLabels")
                  : t("labelPicker.addLabels"))
              }
            />
            <CommandList>
              <CommandEmpty>{t("labelPicker.empty")}</CommandEmpty>
              {topLabels.length > 0 ? (
                <CommandGroup>
                  {topLabels.map((label) => {
                    return (
                      <LabelPickerRow
                        key={label.id}
                        label={label}
                        isSelected={props.selectedIds.includes(label.id)}
                        onToggle={props.onToggle}
                      />
                    );
                  })}
                </CommandGroup>
              ) : null}
              {topLabels.length > 0 && bottomLabels.length > 0 ? (
                <CommandSeparator />
              ) : null}
              {bottomLabels.length > 0 ? (
                <CommandGroup>
                  {bottomLabels.map((label) => {
                    return (
                      <LabelPickerRow
                        key={label.id}
                        label={label}
                        isSelected={props.selectedIds.includes(label.id)}
                        onToggle={props.onToggle}
                      />
                    );
                  })}
                </CommandGroup>
              ) : null}
              {canCreate ? (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      value={trimmedSearch}
                      disabled={createLabelMutation.isPending}
                      onSelect={() => createLabelMutation.mutate(trimmedSearch)}
                    >
                      {createLabelMutation.isPending ? (
                        <Icon name="tabler-loader-2" className="animate-spin" />
                      ) : (
                        <Icon name="tabler-plus" />
                      )}
                      <span className="truncate">
                        {t("labelPicker.create", { name: trimmedSearch })}
                      </span>
                    </CommandItem>
                  </CommandGroup>
                </>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
